  import { useEffect, useState, useMemo } from 'react';
import { Box, Card, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { SiteCard } from 'src/view/components/ProjectDetailComponent';
import SiteArea from 'src/resources/images/area-covered.svg';
import SiteCamera from 'src/resources/images/sitecamera.svg';
import SiteAudio from 'src/resources/images/siteaudio.svg';
import SiteSpecies from 'src/resources/images/sitespecies.svg';
import SiteImage from 'src/resources/images/siteimg.svg';
import SiteFile from 'src/resources/images/sitefile.svg';
import {
  getSpeciesAbundanceRangeCategory,
  //getPotentialRangeCategory,
  //getIntactnessRangeCategory,
  getTaxonomicDissimilarityRangeCategory,
  getHabitatHealthRangeCategory,
  getNDVIRangeCategory,
  getCPLandRangeCategory,
  getHDIRangeCategory
} from 'src/constants/index';
import { convertToNumber } from 'src/constants/index';
import HumanImpact from 'src/resources/images/Human_impact.png';
import Richness from 'src/resources/images/SPECIES_RICHNESS.png';
import HealthHabitat from 'src/resources/images/HABITAT_HEATH.jpeg';
import OverallHealth from 'src/resources/images/OVERALL_HEALTH.png';
import { ProjectInfoCard } from 'src/view/components/cardComponent';
import { useAppState } from 'src/state-management/state-provider';
import {
  BiodiversitySiteAnalysis,
  Site,
  ProjectStatusEnum,
  ChartRequest,
  ChartResponse,
  AcousticIndexEnum,
  UserRole,
} from 'src/api/Api';
import SiteMapWithLayer from 'src/view/DMap/SiteMapWithLayer';
import { useParams } from 'react-router-dom';
import { SubscriptionLock } from 'src/view/components/SubscriptionLock';
import { roundToTwoDecimalPlaces } from 'src/util/util';
import IndexChart from 'src/view/InteractiveMap/IndexChart';
import clientService from 'src/clientService';
import ActivityTrendChart from '../InteractiveMap/ActivityTrendChart';
import { BIODIVERSITY_GLOBAL_LAYERS } from 'src/mapbox-helpers/global-datasets';
import { SampleProjectId } from 'src/enum';

const BiodiversityAnalysisDetail = () => {
  const { state, dispatch } = useAppState();
  const { biodiversitySiteAnalysis, projectDetails } =
    state.selectedProject;
  const [siteBase, setSiteBase] = useState<Site | null>(null);
  const [siteDropDownDetails, setSiteDropDownDetails] = useState<BiodiversitySiteAnalysis>();
  const [selectedDeployment, setSelectedDeployment] = useState<any>(null);
  const [chartData, setChartData] = useState<ChartResponse | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [insituSpeciesData, setInsituSpeciesData] = useState<any>(null);
  const [insituLoading, setInsituLoading] = useState(false);
  const { selectedSite, user } = state;
  const { siteId } = useParams();
  const isLocked = projectDetails?.project_status === ProjectStatusEnum.Draft;


  
  // Check if user role is ONLY User (not Admin or Employee)
  const isUserRole = useMemo(() => {
    if (!user?.role || user.role.length === 0) return false;
    // Show only if user has ONLY User role (no Admin or Employee)
    const roles = user.role;
    const hasUserRole = roles.includes(UserRole.User);
    const hasAdminRole = roles.includes(UserRole.Admin);
    const hasEmployeeRole = roles.includes(UserRole.Employee);
    // Only show if user has User role and doesn't have Admin or Employee
    return hasUserRole && !hasAdminRole && !hasEmployeeRole;
  }, [user]);
  const isSampleProject = projectDetails?.project_id === SampleProjectId;
  // Disable exporting when project is the sample project AND the current user
  // has only the "User" role. Otherwise allow exporting.
  const allowExporting = !(isSampleProject && isUserRole);
  // Ensure Highcharts global exporting option follows our allowExporting flag
  useEffect(() => {
    try {
      Highcharts.setOptions({
        exporting: {
          enabled: allowExporting,
        },
      });
    } catch (e) {
      // Ignore if Highcharts isn't ready; this is defensive
      console.warn('Failed to set Highcharts exporting option', e);
    }
  }, [allowExporting]);
  // Get all project deployments
  const projectDeployments = useMemo(() => {
    return state.selectedProject?.bioMapData?.deployments || [];
  }, [state.selectedProject?.bioMapData]);

  // Get deployments for the selected site by filtering through locations
  const siteDeployments = useMemo(() => {
    const bioMapData = state.selectedProject?.bioMapData;
    if (!projectDeployments || !bioMapData?.locations || !selectedSite?.site_id) return [];

    // First, get all location_ids that belong to the selected site
    const siteLocationIds = bioMapData.locations
      .filter((location) => location.site_id === selectedSite.site_id)
      .map((location) => location.location_id);

    // Then filter deployments that belong to these locations
    return projectDeployments.filter((deployment) =>
      siteLocationIds.includes(deployment.location_id),
    );
  }, [projectDeployments, state.selectedProject?.bioMapData, selectedSite?.site_id]);

  // Fetch chart data for the selected deployment
  const fetchChartData = async (deploymentId: string) => {
    if (!deploymentId) {
      setChartData(null);
      return;
    }
    setChartLoading(true);
    try {
      const req: ChartRequest = {
        deploymentIds: [deploymentId],
        index: [
          AcousticIndexEnum.Aci,
          AcousticIndexEnum.Bi,
          AcousticIndexEnum.Adi,
          AcousticIndexEnum.Aei,
          AcousticIndexEnum.Ndsi,
        ],
      };
      const res =
        await clientService.api.createDeploymentChartsApiV1DeploymentsBioInsituChartPost(req);
      setChartData(res.data);
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setChartData(null);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    const selectedSiteData = biodiversitySiteAnalysis?.find(
      (site: BiodiversitySiteAnalysis) => site.site_id === selectedSite?.site_id,
    );

    const siteBaseSelected = projectDetails?.sites?.find(
      (site: Site) => site.site_id === selectedSite?.site_id,
    );
    setSiteBase(siteBaseSelected || null);
    setSiteDropDownDetails(selectedSiteData);

    // Reset and auto-select first deployment from the site when site changes
    if (siteDeployments.length > 0) {
      const currentDeploymentExists =
        selectedDeployment &&
        siteDeployments.some((d) => d.deployment_id === selectedDeployment.deployment_id);

      if (!currentDeploymentExists) {
        setSelectedDeployment(siteDeployments[0]);
      }
    } else {
      setSelectedDeployment(null);
    }
  }, [siteId, biodiversitySiteAnalysis, siteDeployments, selectedSite?.site_id]);

  // Fetch chart data when deployment changes
  useEffect(() => {
    if (selectedDeployment?.deployment_id) {
      fetchChartData(selectedDeployment.deployment_id);
    }
  }, [selectedDeployment?.deployment_id]);

  // Fetch in-situ species distribution data
  useEffect(() => {
    if (siteId && selectedSite?.site_id) {
      setInsituLoading(true);
      clientService.api
        .readBiodiversityInsituSummaryForSiteApiV1SitesSiteIdBiodiversityInsituSummaryGet(siteId)
        .then((response) => {
          setInsituSpeciesData(response.data);
          setInsituLoading(false);
        })
        .catch((error: any) => {
          console.error('Error fetching in-situ species data:', error);
          setInsituSpeciesData(null);
          setInsituLoading(false);
        });
    } else {
      setInsituSpeciesData(null);
    }
  }, [siteId, selectedSite?.site_id]);

  // Fetch bioMapData if not available
  useEffect(() => {
    if (!state.selectedProject?.bioMapData && projectDetails?.project_id) {
      clientService.api
        .getProjectMapDataApiV1ProjectsProjectIdMapGet(projectDetails.project_id)
        .then((response) => {
          dispatch({
            type: 'SET_PROJECT_MAP_DATA',
            payload: response.data,
          });
        })
        .catch((error) => {
          console.error('Failed to fetch bioMapData:', error);
        });
    }
  }, [projectDetails?.project_id, state.selectedProject?.bioMapData, dispatch]);

  const filterSpeciesData = siteDropDownDetails?.exsitu_species_distribution;

  // Check if species_distribution is empty or has no data
  const hasSpeciesDistributionData = useMemo(() => {
    if (!filterSpeciesData || Object.keys(filterSpeciesData).length === 0) {
      return false;
    }
    // Check if any of the threat level categories have data
    const hasData =
      (filterSpeciesData?.critically_endangered?.[0]?.total &&
        parseInt(filterSpeciesData.critically_endangered[0].total) > 0) ||
      (filterSpeciesData?.endangered?.[0]?.total &&
        parseInt(filterSpeciesData.endangered[0].total) > 0) ||
      (filterSpeciesData?.vulnerable?.[0]?.total &&
        parseInt(filterSpeciesData.vulnerable[0].total) > 0) ||
      (filterSpeciesData?.near_threatened?.[0]?.total &&
        parseInt(filterSpeciesData.near_threatened[0].total) > 0) ||
      (filterSpeciesData?.least_concerned?.[0]?.total &&
        parseInt(filterSpeciesData.least_concerned[0].total) > 0);
    return hasData;
  }, [filterSpeciesData]);

  const speciesData = [
    {
      name: 'Critically Endangered',
      value1: parseInt(filterSpeciesData?.critically_endangered?.[0]?.total || '0'),
      species: filterSpeciesData?.critically_endangered?.[0]?.species_list?.join(', ') || '',
    },
    {
      name: 'Endangered',
      value1: parseInt(filterSpeciesData?.endangered?.[0]?.total || '0'),
      species: filterSpeciesData?.endangered?.[0]?.species_list?.join(', ') || '',
    },
    {
      name: 'Vulnerable',
      value1: parseInt(filterSpeciesData?.vulnerable?.[0]?.total || '0'),
      species: filterSpeciesData?.vulnerable?.[0]?.species_list?.join(', ') || '',
    },
    {
      name: 'Near Threatened',
      value1: parseInt(filterSpeciesData?.near_threatened?.[0]?.total || '0'),
      species: filterSpeciesData?.near_threatened?.[0]?.species_list?.join(', ') || '',
    },
    {
      name: 'Least Concerned',
      value1: parseInt(filterSpeciesData?.least_concerned?.[0]?.total || '0'),
      species: filterSpeciesData?.least_concerned?.[0]?.species_list?.join(', ') || '',
    },
  ];
  const speciesFilterData = speciesData
    .filter((d) => d.value1)
    .map((item) => ({
      name: item.name,
      y: item.value1,
      description: item.species
        .split(', ')
        .map((species: string) => `<br/>- ${species}`)
        .join(''),
    }));

  const speciesOptions = useMemo(() => {
    return {
      chart: {
        type: 'pie',
      },
      // Control exporting per-chart to ensure buttons reflect current permission
      exporting: {
        enabled: allowExporting,
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        pointFormat: `
            Total No.: <b>{point.y}</b><br/>
            Species: <b>{point.description}</b><br/>
            Percentage: <b>{point.percentage:.1f}%</b>
        `,
      },
      legend: {
        enabled: true, // Ensure the legend is enabled
        layout: 'horizontal', // Arrange legends vertically
        align: 'center', // Align legends to the right
        verticalAlign: 'bottom', // Vertically align legends to the middle
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            distance: -30,
            enabled: true,
            format: '<b>{point.y}</b><br/>',
            style: {
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 'bold',
            },
          },
          innerSize: '50%', // For donut effect
        },
      },
      series: [
        {
          name: 'Species',
          colorByPoint: true,
          showInLegend: true, // Enable legends for this series
          data: speciesFilterData, // Ensure this contains valid data
        },
      ],
    };
  }, [speciesFilterData, allowExporting]);

  // Process in-situ species distribution data
  const insituSpeciesDistributionData = useMemo(() => {
    if (!insituSpeciesData) return null;
    
    const insituData = [
      {
        name: 'Critically Endangered',
        value1: parseInt(insituSpeciesData?.critically_endangered?.[0]?.total || '0'),
        species: insituSpeciesData?.critically_endangered?.[0]?.species_list?.join(', ') || '',
      },
      {
        name: 'Endangered',
        value1: parseInt(insituSpeciesData?.endangered?.[0]?.total || '0'),
        species: insituSpeciesData?.endangered?.[0]?.species_list?.join(', ') || '',
      },
      {
        name: 'Vulnerable',
        value1: parseInt(insituSpeciesData?.vulnerable?.[0]?.total || '0'),
        species: insituSpeciesData?.vulnerable?.[0]?.species_list?.join(', ') || '',
      },
      {
        name: 'Near Threatened',
        value1: parseInt(insituSpeciesData?.near_threatened?.[0]?.total || '0'),
        species: insituSpeciesData?.near_threatened?.[0]?.species_list?.join(', ') || '',
      },
      {
        name: 'Least Concerned',
        value1: parseInt(insituSpeciesData?.least_concerned?.[0]?.total || '0'),
        species: insituSpeciesData?.least_concerned?.[0]?.species_list?.join(', ') || '',
      },
      {
        name: 'Unknown',
        value1: parseInt(insituSpeciesData?.unknown?.[0]?.total || '0'),
        species: insituSpeciesData?.unknown?.[0]?.species_list?.join(', ') || '',
      },
    ];

    return insituData
      .filter((d) => d.value1)
      .map((item) => ({
        name: item.name,
        y: item.value1,
        description: item.species
          .split(', ')
          .map((species: string) => `<br/>- ${species}`)
          .join(''),
      }));
  }, [insituSpeciesData]);

  const hasInsituData = useMemo(() => {
    return insituSpeciesDistributionData && insituSpeciesDistributionData.length > 0;
  }, [insituSpeciesDistributionData]);

  const insituSpeciesOptions = useMemo(() => {
    return {
      chart: {
        type: 'pie',
      },
      // Control exporting per-chart to ensure buttons reflect current permission
      exporting: {
        enabled: allowExporting,
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        pointFormat: `
            Total No.: <b>{point.y}</b><br/>
            Species: <b>{point.description}</b><br/>
            Percentage: <b>{point.percentage:.1f}%</b>
        `,
      },
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            distance: -30,
            enabled: true,
            format: '<b>{point.y}</b><br/>',
            style: {
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 'bold',
            },
          },
          innerSize: '50%',
        },
      },
      series: [
        {
          name: 'Species',
          colorByPoint: true,
          showInLegend: true,
          data: insituSpeciesDistributionData || [],
        },
      ],
    };
  }, [insituSpeciesDistributionData, allowExporting]);



  const LockChart = (
    <SubscriptionLock
      message="Register your project with us to unlock advanced features and insights."
      ctaLabel="Register Project"
      onCtaClick={() => window.open('https://calendly.com/jaya-kandir-darukaa/new-meeting', '_blank')}
    />
  );
  return (
    <Box sx={{ mt: 4 }}>
      {siteBase && (
        <Box className="bio-map" sx={{ mt: 2 }} height={600}>
          <SiteMapWithLayer
            sites={[siteBase]}
            fitToBoundary={true}
            showDevices={true}
            rasterLayer={BIODIVERSITY_GLOBAL_LAYERS}
          ></SiteMapWithLayer>
        </Box>
      )}
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ m: 1.5 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SiteCard
            title={'Area Covered'}
            amount={`${roundToTwoDecimalPlaces(siteBase?.area || 0)} ${projectDetails?.area_unit}`}
            icon={SiteArea}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SiteCard
            title={'Cameras Installed'}
            amount={siteDropDownDetails?.camera_installed || 0}
            icon={SiteCamera}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SiteCard
            title={'Audio Devices'}
            amount={siteDropDownDetails?.audio_devices || 0}
            icon={SiteAudio}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SiteCard
            title={'Species Identified (In-situ)'}
              amount={siteDropDownDetails?.insitu_data?.[0]?.species_count || 0}
            icon={SiteSpecies}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SiteCard title={'Images & Videos'} amount={siteDropDownDetails?.total_images_videos || 0} icon={SiteImage} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SiteCard title={'Audio Files'} amount={siteDropDownDetails?.total_audiofiles || 0} icon={SiteFile} />
        </Grid>
      </Grid>

      <Box
        sx={{
          overflowY: 'auto',
          border: '1px solid #ccc',
          padding: '2px',
          backgroundColor: '#fff',
          margin: '10px',
          borderRadius: '8px',
          boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
        }}
      >
        <Grid container>
          <ProjectInfoCard
            imageSrc={OverallHealth}
            title="Ecosystem Health"
            data={[
              {
                label: 'Habitat Health Index',
                value: roundToTwoDecimalPlaces(siteDropDownDetails?.habitat_health || 0),
                tooltip:
                  'Assesses the condition and sustainability of habitats for supporting biodiversity.',
                color: getHabitatHealthRangeCategory(
                  convertToNumber(siteDropDownDetails?.habitat_health ?? 0),
                ).color,
              },
              {
                label: 'Normalized Difference Vegetation Index',
                value: roundToTwoDecimalPlaces(siteDropDownDetails?.mean_ndvi || 0),
                tooltip:
                  'Standardized numerical indicator that quantifies the health and density of live green vegetation.',
                color: getNDVIRangeCategory(
                  convertToNumber(siteDropDownDetails?.mean_ndvi ?? 0),
                ).color,
              },
              {
                label: 'Landscape Fragmentation Index',
                value: roundToTwoDecimalPlaces(siteDropDownDetails?.cp_land || 0),
                tooltip:
                  'Measures the structural integrity of a landscape by assessing the degree to which natural habitats are broken into smaller, isolated patches.',
                color: getCPLandRangeCategory(
                  convertToNumber(siteDropDownDetails?.cp_land ?? 0),
                ).color,
              }
            ]}
          />

          <ProjectInfoCard
            imageSrc={Richness}
            title="Species Assemblage"
            data={[
              {
                label: 'Species Diversity Index',
                value: roundToTwoDecimalPlaces(siteDropDownDetails?.species_diversity || 0),
                tooltip:
                  'Reflects the variety of species within a specific area, accounting for their abundance.',
                color: getSpeciesAbundanceRangeCategory(
                  convertToNumber(siteDropDownDetails?.species_diversity ?? 0),
                ).color,
              },
              {
                label: 'Species Richness Index',
                value: roundToTwoDecimalPlaces(siteDropDownDetails?.species_richness || 0),
                tooltip:
                  'Represents the total number of different species in a given area, without considering their abundance.',
                color: getSpeciesAbundanceRangeCategory(
                  convertToNumber(siteDropDownDetails?.species_richness ?? 0),
                ).color,
              },
              {
                label: 'Taxonomic Dissimilarity',
                value: roundToTwoDecimalPlaces(siteDropDownDetails?.taxonomic_dissimilarity || 0),
                tooltip:
                  'Measures the evolutionary or taxonomic differences between species within an ecosystem.',
                color: getTaxonomicDissimilarityRangeCategory(
                  convertToNumber(siteDropDownDetails?.taxonomic_dissimilarity ?? 0),
                ).color,
              },
            ]}
          />
          <ProjectInfoCard
            imageSrc={HealthHabitat}
            title="Species Status"
            data={[
              {
                label: 'Mean Species Abundance',
                value: roundToTwoDecimalPlaces(siteDropDownDetails?.mean_species_abundance || 0),
                tooltip:
                  'The average abundance of native species in the area compared to their abundance in an undisturbed ecosystem.',
                color: getSpeciesAbundanceRangeCategory(
                  convertToNumber(siteDropDownDetails?.mean_species_abundance ?? 0),
                ).color,
              },
              //{
                //label: 'Biodiversity Intactness Index',
                //value: roundToTwoDecimalPlaces(
                  //siteDropDownDetails?.biodiversity_intactness_index || 0,
                //),
                //tooltip:
                //  'A measure of how much of the original biodiversity remains intact in the ecosystem, relative to undisturbed conditions.',
                //color: getIntactnessRangeCategory(
                //  convertToNumber(siteDropDownDetails?.biodiversity_intactness_index ?? 0),
                //).color,
              //},
            ]}
          />
          {/* TODO: Do not remove, will enable later */}

          <ProjectInfoCard
            imageSrc={HumanImpact}
            title="Threats"
            data={[
              //{
                //label: 'Potential Disappearance Fraction',
                //value: roundToTwoDecimalPlaces(
                 // biodiversityProjectAnalysis?.potential_disappearance_fraction || 0,
                //),
                //tooltip:
                 // 'The estimated proportion of species at risk of disappearing due to environmental degradation or human impact.',
               // color: getPotentialRangeCategory(
                 // convertToNumber(
                  //  biodiversityProjectAnalysis?.potential_disappearance_fraction ?? 0,
                 // ),
                //).color,
             // },
              {
                label: 'Human Disturbance Index',
                value: roundToTwoDecimalPlaces(
                  siteDropDownDetails?.hdi || 0,
                ),
                tooltip:
                  'A normalized proximity metric that quantifies the ecological "buffer" between a specific site and the nearest urban built-up area.',
                color: getHDIRangeCategory(
                  convertToNumber(
                    siteDropDownDetails?.hdi ?? 0,
                  ),
                ).color,
              }
            ]}
          />
        </Grid>
      </Box>

      <Box sx={{ m: 1 }}>
        <Grid container spacing={3}>
          {/* Species Distribution by Threat Level - Ex-Situ */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <Typography
                component="div"
                sx={{
                  textAlign: 'left',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#151E15',
                  p: '16px',
                }}
              >
                {'Species Distribution by Threat Level (Ex-Situ)'}
              </Typography>
              <Box className="divider" sx={{ p: 2 }} />
              {hasSpeciesDistributionData && speciesFilterData.length > 0 ? (
                <HighchartsReact highcharts={Highcharts} options={speciesOptions} />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#666',
                      fontWeight: 500,
                    }}
                  >
                    No Ex-situ data found
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>

          {/* Species Distribution by Threat Level - In-Situ */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <Typography
                component="div"
                sx={{
                  textAlign: 'left',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#151E15',
                  p: '16px',
                }}
              >
                {'Species Distribution by Threat Level (In-Situ)'}
              </Typography>
              <Box className="divider" sx={{ p: 2 }} />
              {insituLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#666',
                      fontWeight: 500,
                    }}
                  >
                    Loading...
                  </Typography>
                </Box>
              ) : hasInsituData ? (
                <HighchartsReact highcharts={Highcharts} options={insituSpeciesOptions} />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#666',
                      fontWeight: 500,
                    }}
                  >
                    No In-situ data found
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>

          {/* Site-Level In-Situ Charts - Only show for User role */}


          {/* New Deployment Charts */}
          {(isLocked || selectedDeployment || projectDeployments.length > 0) && (
            <>
              {/* Index Chart */}
              <Grid size={{ xs: 12 }}>
                {isLocked ? (
                  <>
                    <Typography
                      component="div"
                      sx={{
                        textAlign: 'left',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#151E15',
                        p: '16px',
                      }}
                    >
                      {'Acoustic Index Analysis'}
                    </Typography>
                    <Box className="divider" sx={{ p: 2 }} />
                    {LockChart}
                  </>
                ) : (
                  <Card
                    sx={{
                      boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Typography
                      component="div"
                      sx={{
                        textAlign: 'left',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#151E15',
                        p: '16px',
                      }}
                    >
                      {'Acoustic Index Analysis'}
                    </Typography>
                    <Box className="divider" sx={{ p: 2 }} />
                    {chartLoading ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '400px',
                        }}
                      >
                        <Typography>Loading chart data...</Typography>
                      </Box>
                      ) : chartData ? (
                      <IndexChart
                        chartData={chartData}
                        deploymentName={
                          selectedDeployment?.deployment_name ||
                          selectedDeployment?.deployment_id ||
                          'Deployment'
                        }
                        allowExporting={allowExporting}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '400px',
                        }}
                      >
                        <Typography>No chart data available</Typography>
                      </Box>
                    )}
                  </Card>
                )}
              </Grid>

              {/* Activity Trend Chart */}
              <Grid size={{ xs: 12 }}>
                {isLocked ? (
                  <>
                    <Typography
                      component="div"
                      sx={{
                        textAlign: 'left',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#151E15',
                        p: '16px',
                      }}
                    >
                      {'Species Activity Trend Analysis'}
                    </Typography>
                    <Box className="divider" sx={{ p: 2 }} />
                    {LockChart}
                  </>
                ) : (
                  <Card
                    sx={{
                      boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Typography
                      component="div"
                      sx={{
                        textAlign: 'left',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#151E15',
                        p: '16px',
                      }}
                    >
                      {'Species Activity Trend Analysis'}
                    </Typography>
                    <Box className="divider" sx={{ p: 2 }} />
                    {chartData ? (
                      <ActivityTrendChart
                        chartData={chartData}
                        deploymentName={selectedDeployment?.deployment_name || 'Deployment'}
                        allowExporting={allowExporting}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '400px',
                        }}
                      >
                        <Typography>No chart data available</Typography>
                      </Box>
                    )}
                  </Card>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default BiodiversityAnalysisDetail;
